import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Download, Upload, FileSpreadsheet, ArrowRight, ArrowLeft, Check,
    Loader2, AlertCircle, ChevronDown, X, Sparkles, CheckCircle2, Plus,
    Search, Eye, AlertTriangle
} from 'lucide-react';
import { Button } from './ui/button';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function ImportWizard({ onGoToStaging }) {
    const { getAuthHeader } = useAuth();
    const [step, setStep] = useState(1);

    // Step 1: Fields
    const [accountFields, setAccountFields] = useState([]);
    const [contactFields, setContactFields] = useState([]);
    const [selectedFields, setSelectedFields] = useState([]);
    const [loadingFields, setLoadingFields] = useState(true);

    // Step 2: Upload
    const [file, setFile] = useState(null);
    const [previewRows, setPreviewRows] = useState([]);
    const [previewCols, setPreviewCols] = useState([]);

    // Step 3: Mapping
    const [mappings, setMappings] = useState([]);
    const [allFields, setAllFields] = useState([]);
    const [mapping, setMapping] = useState(false);
    const [totalRows, setTotalRows] = useState(0);

    // Step 4: Preview
    const [previewDataRows, setPreviewDataRows] = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [previewSearchTerm, setPreviewSearchTerm] = useState('');
    const [editingCell, setEditingCell] = useState(null); // {rowIdx, colKey}

    // Step 5: Import to Staging
    const [listName, setListName] = useState('');
    const [duplicateMode, setDuplicateMode] = useState('update'); // 'skip' | 'update' | 'import_all'
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);

    // Load fields on mount
    useEffect(() => {
        const loadFields = async () => {
            try {
                const res = await axios.get(`${API}/api/import/fields`, { headers: getAuthHeader() });
                setAccountFields(res.data.account_fields || []);
                setContactFields(res.data.contact_fields || []);
                setAllFields([...(res.data.account_fields || []), ...(res.data.contact_fields || [])]);
                // Pre-select essential fields
                setSelectedFields(['company_name', 'first_name', 'last_name', 'email', 'title', 'industry']);
            } catch { toast.error('Failed to load import fields'); }
            finally { setLoadingFields(false); }
        };
        loadFields();
    }, [getAuthHeader]);

    const toggleField = (key) => {
        setSelectedFields(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        );
    };

    const selectPreset = (preset) => {
        if (preset === 'all_account') {
            setSelectedFields(prev => [...new Set([...prev, ...accountFields.map(f => f.key)])]);
        } else if (preset === 'all_contact') {
            setSelectedFields(prev => [...new Set([...prev, ...contactFields.map(f => f.key)])]);
        } else if (preset === 'essential') {
            setSelectedFields(['company_name', 'first_name', 'last_name', 'email', 'title', 'industry', 'city', 'country']);
        }
    };

    const downloadTemplate = async () => {
        try {
            const res = await axios.post(`${API}/api/import/template`,
                { fields: selectedFields },
                { headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }, responseType: 'blob' }
            );
            const url = window.URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'import_template.csv';
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success('Template downloaded');
        } catch { toast.error('Failed to download template'); }
    };

    // File upload and preview
    const handleFileSelect = (selectedFile) => {
        if (!selectedFile) return;
        setFile(selectedFile);
        // Parse preview
        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target.result;
            const lines = text.split('\n').filter(l => l.trim());
            if (lines.length === 0) return;
            const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
            setPreviewCols(headers);
            const rows = [];
            for (let i = 1; i < Math.min(lines.length, 6); i++) {
                const vals = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
                const row = {};
                headers.forEach((h, idx) => { row[h] = vals[idx] || ''; });
                rows.push(row);
            }
            setPreviewRows(rows);
        };
        reader.readAsText(selectedFile);
    };

    // Auto-map
    const runAutoMap = async () => {
        if (!file) return;
        setMapping(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await axios.post(`${API}/api/import/auto-map`, formData, {
                headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
            });
            setMappings(res.data.mappings || []);
            setTotalRows(res.data.total_rows || 0);
            setAllFields(res.data.available_fields || allFields);
            setStep(3);
        } catch { toast.error('Auto-mapping failed'); }
        finally { setMapping(false); }
    };

    const updateMapping = (index, newCrmField) => {
        setMappings(prev => prev.map((m, i) =>
            i === index ? { ...m, crm_field: newCrmField, crm_field_label: allFields.find(f => f.key === newCrmField)?.label || newCrmField, confidence: newCrmField ? 100 : 0 } : m
        ));
    };

    const handleCreateCustomField = async (mappingIndex, csvColumn, entity) => {
        // Auto-create a custom field from the unmapped CSV column
        const label = csvColumn.trim();
        const key = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
        try {
            await axios.post(`${API}/api/admin/custom-fields`, {
                entity,
                label,
                field_type: 'text',
                required: false,
            }, { headers: getAuthHeader() });

            // Add the new field to allFields
            const newField = { key: `cf_${key}`, label, type: 'text', entity, is_custom: true, example: '' };
            setAllFields(prev => [...prev, newField]);

            // Update the mapping to point to the new field
            setMappings(prev => prev.map((m, i) =>
                i === mappingIndex ? { ...m, crm_field: `cf_${key}`, crm_field_label: label, confidence: 100 } : m
            ));

            toast.success(`Created custom ${entity} field: ${label}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || `Failed to create custom field "${label}"`);
        }
    };

    // Edit a cell in the preview table
    const updatePreviewCell = (rowNum, colKey, newValue) => {
        setPreviewDataRows(prev => {
            if (!prev) return prev;
            const updatedRows = prev.rows.map(r => {
                if (r.row_num !== rowNum) return r;
                const updatedFields = { ...r.fields, [colKey]: newValue };
                // Recompute warning
                const hasName = !!(updatedFields.first_name || updatedFields.last_name);
                const hasCompany = !!updatedFields.company_name;
                const hasEmail = !!updatedFields.email;
                let warning = null;
                if (!hasName && !hasCompany) warning = 'Missing name and company';
                else if (!hasEmail && hasName) warning = 'No email address';
                return { ...r, fields: updatedFields, warning };
            });
            const warningCount = updatedRows.filter(r => r.warning).length;
            return { ...prev, rows: updatedRows, warning_count: warningCount };
        });
    };

    // Load preview data (step 4)
    const loadPreview = async () => {
        const activeMappings = mappings.filter(m => m.crm_field);
        if (activeMappings.length === 0) {
            toast.error('No fields mapped');
            return;
        }
        setLoadingPreview(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('config', JSON.stringify({ mappings: activeMappings }));
            const res = await axios.post(`${API}/api/import/preview`, formData, {
                headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
            });
            setPreviewDataRows(res.data);
            setStep(4);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Preview failed');
        } finally {
            setLoadingPreview(false);
        }
    };

    // Download edited preview data as CSV
    const downloadPreviewCSV = () => {
        if (!previewDataRows) return;
        const cols = previewDataRows.columns;
        const header = cols.map(c => `"${c.label}"`).join(',');
        const rows = previewDataRows.rows.map(r =>
            cols.map(c => {
                const val = (r.fields[c.key] || '').replace(/"/g, '""');
                return `"${val}"`;
            }).join(',')
        );
        const csv = [header, ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${listName || 'import_preview'}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('CSV downloaded');
    };

    // Execute import
    const executeImport = async () => {
        const activeMappings = mappings.filter(m => m.crm_field);
        if (activeMappings.length === 0) {
            toast.error('No fields mapped');
            return;
        }
        setImporting(true);
        try {
            // Build mapped rows from preview data or raw file
            let rows = [];
            if (previewDataRows?.rows) {
                rows = previewDataRows.rows.map(r => r.fields);
            } else {
                // Fallback: use the preview endpoint to get mapped data
                const formData = new FormData();
                formData.append('file', file);
                formData.append('config', JSON.stringify({ mappings: activeMappings }));
                const previewRes = await axios.post(`${API}/api/import/preview`, formData, {
                    headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
                });
                rows = (previewRes.data?.rows || []).map(r => r.fields);
            }

            const payload = {
                list_name: listName || 'Imported List',
                duplicate_mode: duplicateMode,
                rows,
            };

            const res = await axios.post(`${API}/api/staging/import-wizard`, payload, {
                headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
            });
            setImportResult(res.data);
            toast.success(`${res.data.staged_rows} records sent to staging for review`);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Import failed');
        }
        finally { setImporting(false); }
    };

    const reset = () => {
        setStep(1);
        setFile(null);
        setPreviewRows([]);
        setPreviewCols([]);
        setMappings([]);
        setPreviewDataRows(null);
        setPreviewSearchTerm('');
        setImportResult(null);
        setListName('');
    };

    const confidenceColor = (c) => {
        if (c >= 80) return 'bg-emerald-100 text-emerald-700';
        if (c >= 50) return 'bg-amber-100 text-amber-700';
        return 'bg-red-100 text-red-700';
    };

    if (loadingFields) {
        return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-stone-400 animate-spin" /></div>;
    }

    return (
        <div className="space-y-6">
            {/* Step Indicator */}
            <div className="flex items-center gap-2">
                {[
                    { n: 1, label: 'Select Fields' },
                    { n: 2, label: 'Upload CSV' },
                    { n: 3, label: 'Map Fields' },
                    { n: 4, label: 'Preview Data' },
                    { n: 5, label: 'Import' },
                ].map(({ n, label }) => (
                    <React.Fragment key={n}>
                        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                            step === n ? 'bg-stone-900 text-white' :
                            step > n ? 'bg-emerald-100 text-emerald-700' :
                            'bg-stone-100 text-stone-400'
                        }`}>
                            {step > n ? <Check className="w-3 h-3" /> : <span>{n}</span>}
                            <span className="hidden sm:inline">{label}</span>
                        </div>
                        {n < 5 && <div className={`flex-1 h-px ${step > n ? 'bg-emerald-300' : 'bg-stone-200'}`} />}
                    </React.Fragment>
                ))}
            </div>

            {/* Step 1: Select Fields */}
            {step === 1 && (
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-sm font-semibold text-stone-900">Select import fields</h3>
                            <p className="text-xs text-stone-500 mt-0.5">Choose which fields your CSV will contain. Download a template with these columns.</p>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={() => selectPreset('essential')} className="text-xs px-2.5 py-1 rounded-lg border border-stone-200 text-stone-600 hover:bg-stone-50">Essential</button>
                            <button onClick={() => selectPreset('all_account')} className="text-xs px-2.5 py-1 rounded-lg border border-stone-200 text-stone-600 hover:bg-stone-50">All Company</button>
                            <button onClick={() => selectPreset('all_contact')} className="text-xs px-2.5 py-1 rounded-lg border border-stone-200 text-stone-600 hover:bg-stone-50">All Contact</button>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Account Fields */}
                        <div className="bg-stone-50 rounded-lg p-4">
                            <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">Company Fields</h4>
                            <div className="space-y-1.5">
                                {accountFields.map(f => (
                                    <label key={f.key} className="flex items-center gap-2.5 py-1.5 px-2 rounded-lg hover:bg-white cursor-pointer text-sm">
                                        <input type="checkbox" checked={selectedFields.includes(f.key)} onChange={() => toggleField(f.key)}
                                            className="rounded border-stone-300 text-stone-900 focus:ring-stone-900" />
                                        <span className="text-stone-700">{f.label}</span>
                                        {f.required && <span className="text-xs text-red-500">*</span>}
                                        <span className="text-xs text-stone-400 ml-auto">{f.example}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Contact Fields */}
                        <div className="bg-stone-50 rounded-lg p-4">
                            <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">Contact Fields</h4>
                            <div className="space-y-1.5">
                                {contactFields.map(f => (
                                    <label key={f.key} className="flex items-center gap-2.5 py-1.5 px-2 rounded-lg hover:bg-white cursor-pointer text-sm">
                                        <input type="checkbox" checked={selectedFields.includes(f.key)} onChange={() => toggleField(f.key)}
                                            className="rounded border-stone-300 text-stone-900 focus:ring-stone-900" />
                                        <span className="text-stone-700">{f.label}</span>
                                        {f.required && <span className="text-xs text-red-500">*</span>}
                                        <span className="text-xs text-stone-400 ml-auto">{f.example}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center justify-between pt-2">
                        <Button variant="outline" onClick={downloadTemplate} disabled={selectedFields.length === 0}>
                            <Download className="w-4 h-4 mr-1.5" /> Download CSV Template
                        </Button>
                        <Button onClick={() => setStep(2)} disabled={selectedFields.length === 0}>
                            Next: Upload CSV <ArrowRight className="w-4 h-4 ml-1.5" />
                        </Button>
                    </div>
                </div>
            )}

            {/* Step 2: Upload CSV */}
            {step === 2 && (
                <div className="space-y-4">
                    <div>
                        <h3 className="text-sm font-semibold text-stone-900">Upload your CSV file</h3>
                        <p className="text-xs text-stone-500 mt-0.5">Upload a CSV with your data. Column names don't need to match exactly — we'll auto-map them next.</p>
                    </div>

                    <div
                        className={`border-2 border-dashed rounded-xl p-10 text-center transition-colors ${
                            file ? 'border-emerald-300 bg-emerald-50' : 'border-stone-200 hover:border-stone-400'
                        }`}
                        onDrop={(e) => { e.preventDefault(); handleFileSelect(e.dataTransfer.files[0]); }}
                        onDragOver={(e) => e.preventDefault()}
                    >
                        {file ? (
                            <div className="space-y-2">
                                <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
                                <p className="text-sm font-medium text-emerald-700">{file.name}</p>
                                <p className="text-xs text-emerald-600">{(file.size / 1024).toFixed(1)} KB</p>
                                <button onClick={() => { setFile(null); setPreviewRows([]); setPreviewCols([]); }}
                                    className="text-xs text-stone-500 underline hover:text-stone-700">Remove</button>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                <FileSpreadsheet className="w-10 h-10 text-stone-300 mx-auto" />
                                <p className="text-sm text-stone-500">Drag & drop your CSV here, or</p>
                                <label className="inline-flex items-center gap-1.5 px-4 py-2 bg-stone-900 text-white text-sm font-medium rounded-lg cursor-pointer hover:bg-stone-800">
                                    <Upload className="w-4 h-4" /> Browse files
                                    <input type="file" accept=".csv" className="hidden" onChange={(e) => handleFileSelect(e.target.files[0])} />
                                </label>
                            </div>
                        )}
                    </div>

                    {/* Preview Table */}
                    {previewRows.length > 0 && (
                        <div className="bg-stone-50 rounded-lg p-4">
                            <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">Preview (first {previewRows.length} rows)</h4>
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr>{previewCols.map(col => <th key={col} className="text-left px-2 py-1.5 font-medium text-stone-600 border-b border-stone-200">{col}</th>)}</tr>
                                    </thead>
                                    <tbody>
                                        {previewRows.map((row, i) => (
                                            <tr key={i} className="border-b border-stone-100">
                                                {previewCols.map(col => <td key={col} className="px-2 py-1.5 text-stone-700 max-w-[150px] truncate">{row[col]}</td>)}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    <div className="flex items-center justify-between pt-2">
                        <Button variant="outline" onClick={() => setStep(1)}>
                            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
                        </Button>
                        <Button onClick={runAutoMap} disabled={!file || mapping}>
                            {mapping ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Analyzing...</> :
                            <><Sparkles className="w-4 h-4 mr-1.5" /> Auto-Map Fields</>}
                        </Button>
                    </div>
                </div>
            )}

            {/* Step 3: Map Fields */}
            {step === 3 && (
                <div className="space-y-4">
                    <div>
                        <h3 className="text-sm font-semibold text-stone-900">Review field mapping</h3>
                        <p className="text-xs text-stone-500 mt-0.5">{totalRows} rows detected. Verify the auto-detected mappings and adjust if needed.</p>
                    </div>

                    {(() => {
                        const unmapped = mappings.filter(m => !m.crm_field);
                        if (unmapped.length > 0) return (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                                    <span className="text-xs text-amber-800">
                                        <strong>{unmapped.length} unmapped column{unmapped.length > 1 ? 's' : ''}</strong>: {unmapped.map(m => m.csv_column).join(', ')}.
                                        Use <strong>"+ Create Field"</strong> to import them as custom fields.
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    onClick={async () => {
                                        for (let i = 0; i < mappings.length; i++) {
                                            if (!mappings[i].crm_field) {
                                                await handleCreateCustomField(i, mappings[i].csv_column, 'contact');
                                            }
                                        }
                                    }}
                                    className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-amber-200 hover:bg-amber-300 text-amber-900 rounded-lg transition-colors whitespace-nowrap"
                                >
                                    <Plus className="w-3 h-3" /> Create All
                                </button>
                            </div>
                        );
                        return null;
                    })()}

                    <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-stone-50">
                                <tr>
                                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase">CSV Column</th>
                                    <th className="text-center px-2 py-2.5 text-xs text-stone-400 w-8">&rarr;</th>
                                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase">CRM Field</th>
                                    <th className="text-center px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase">Confidence</th>
                                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase">Sample</th>
                                </tr>
                            </thead>
                            <tbody>
                                {mappings.map((m, i) => (
                                    <tr key={i} className="border-t border-stone-100">
                                        <td className="px-4 py-2.5 font-medium text-stone-900">{m.csv_column}</td>
                                        <td className="text-center text-stone-300">&rarr;</td>
                                        <td className="px-4 py-2.5">
                                            <div className="flex gap-1.5 items-center">
                                                <select
                                                    value={m.crm_field || ''}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        if (val === '__create_contact_cf__' || val === '__create_account_cf__') {
                                                            handleCreateCustomField(i, m.csv_column, val === '__create_account_cf__' ? 'account' : 'contact');
                                                        } else {
                                                            updateMapping(i, val || null);
                                                        }
                                                    }}
                                                    className={`flex-1 min-w-0 px-2 py-1.5 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20 ${
                                                        m.crm_field ? 'border-stone-200' : 'border-amber-300 bg-amber-50'
                                                    }`}
                                                >
                                                    <option value="">-- Ignore --</option>
                                                    <optgroup label="Company Fields">
                                                        {allFields.filter(f => f.entity === 'account' && !f.is_custom).map(f =>
                                                            <option key={f.key} value={f.key}>{f.label}</option>
                                                        )}
                                                    </optgroup>
                                                    <optgroup label="Contact Fields">
                                                        {allFields.filter(f => f.entity === 'contact' && !f.is_custom).map(f =>
                                                            <option key={f.key} value={f.key}>{f.label}</option>
                                                        )}
                                                    </optgroup>
                                                    {allFields.some(f => f.is_custom && f.entity === 'account') && (
                                                        <optgroup label="Custom Company Fields">
                                                            {allFields.filter(f => f.is_custom && f.entity === 'account').map(f =>
                                                                <option key={f.key} value={f.key}>⬥ {f.label}</option>
                                                            )}
                                                        </optgroup>
                                                    )}
                                                    {allFields.some(f => f.is_custom && f.entity === 'contact') && (
                                                        <optgroup label="Custom Contact Fields">
                                                            {allFields.filter(f => f.is_custom && f.entity === 'contact').map(f =>
                                                                <option key={f.key} value={f.key}>⬥ {f.label}</option>
                                                            )}
                                                        </optgroup>
                                                    )}
                                                    <optgroup label="Create New Custom Field">
                                                        <option value="__create_contact_cf__">+ Create as Contact field</option>
                                                        <option value="__create_account_cf__">+ Create as Company field</option>
                                                    </optgroup>
                                                </select>
                                                {!m.crm_field && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleCreateCustomField(i, m.csv_column, 'contact')}
                                                        className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-lg transition-colors whitespace-nowrap"
                                                        title={`Create "${m.csv_column}" as a new custom contact field`}
                                                    >
                                                        <Plus className="w-3 h-3" /> Create Field
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-2.5 text-center">
                                            {m.crm_field && (
                                                <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${confidenceColor(m.confidence)}`}>
                                                    {m.confidence}%
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-2.5 text-xs text-stone-400 max-w-[200px] truncate">
                                            {(m.sample_values || []).join(', ')}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex items-center justify-between pt-2">
                        <Button variant="outline" onClick={() => setStep(2)}>
                            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
                        </Button>
                        <Button onClick={loadPreview} disabled={loadingPreview || mappings.filter(m => m.crm_field).length === 0}>
                            {loadingPreview ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Loading Preview...</> :
                            <><Eye className="w-4 h-4 mr-1.5" /> Preview Data <ArrowRight className="w-4 h-4 ml-1.5" /></>}
                        </Button>
                    </div>
                </div>
            )}

            {/* Step 4: Preview Data */}
            {step === 4 && previewDataRows && (
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-sm font-semibold text-stone-900">Preview import data</h3>
                            <p className="text-xs text-stone-500 mt-0.5">Review all {previewDataRows.total_rows} rows before importing. Scroll through to verify the data looks correct.</p>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-1.5">
                                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                                <span className="text-xs font-medium text-emerald-800">{previewDataRows.total_rows - previewDataRows.warning_count} ready</span>
                            </div>
                            {previewDataRows.warning_count > 0 && (
                                <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
                                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                                    <span className="text-xs font-medium text-amber-800">{previewDataRows.warning_count} warnings</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Status Legend */}
                    {previewDataRows.warning_count > 0 && (
                        <div className="bg-stone-50 border border-stone-200 rounded-lg p-3">
                            <p className="text-xs font-semibold text-stone-700 mb-2">What do the status icons mean?</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <div className="flex items-start gap-2">
                                    <span className="inline-flex items-center mt-0.5 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 flex-shrink-0">
                                        <CheckCircle2 className="w-3 h-3" />
                                    </span>
                                    <div>
                                        <p className="text-xs font-medium text-stone-700">Ready to import</p>
                                        <p className="text-[11px] text-stone-500">This row has all key fields (name, company, or email) and will be imported normally.</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-2">
                                    <span className="inline-flex items-center mt-0.5 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 flex-shrink-0">
                                        <AlertTriangle className="w-3 h-3" />
                                    </span>
                                    <div>
                                        <p className="text-xs font-medium text-stone-700">Warning — may be incomplete</p>
                                        <p className="text-[11px] text-stone-500">This row is missing important data. It will still be imported, but may create incomplete records. Hover the icon to see what's missing.</p>
                                    </div>
                                </div>
                            </div>
                            <div className="mt-2 pt-2 border-t border-stone-200">
                                <p className="text-[11px] text-stone-500"><strong>Common warnings:</strong> "Missing name and company" means the row has no first/last name or company — the contact record may be hard to identify. "No email address" means the contact can be created but won't be reachable via email campaigns.</p>
                            </div>
                        </div>
                    )}

                    {/* Search */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                        <input
                            type="text"
                            placeholder="Search records..."
                            value={previewSearchTerm}
                            onChange={e => setPreviewSearchTerm(e.target.value)}
                            className="w-full pl-9 pr-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-500"
                        />
                    </div>

                    {/* Preview Table */}
                    <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
                        <div className="max-h-[450px] overflow-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-stone-50 border-b border-stone-200 sticky top-0 z-10">
                                    <tr>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-stone-500 w-12">#</th>
                                        {previewDataRows.columns.map(col => (
                                            <th key={col.key} className="px-3 py-2 text-left text-xs font-semibold text-stone-500 whitespace-nowrap">{col.label}</th>
                                        ))}
                                        <th className="px-3 py-2 text-center text-xs font-semibold text-stone-500 w-24">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-stone-100">
                                    {(previewSearchTerm
                                        ? previewDataRows.rows.filter(r =>
                                            Object.values(r.fields).some(v => v && String(v).toLowerCase().includes(previewSearchTerm.toLowerCase()))
                                        )
                                        : previewDataRows.rows
                                    ).map(row => (
                                        <tr key={row.row_num} className={`hover:bg-stone-50 ${row.warning ? 'bg-amber-50/50' : ''}`}>
                                            <td className="px-3 py-1.5 text-xs text-stone-400">{row.row_num}</td>
                                            {previewDataRows.columns.map(col => (
                                                <td key={col.key} className="px-0.5 py-0.5">
                                                    {editingCell?.rowIdx === row.row_num && editingCell?.colKey === col.key ? (
                                                        <input
                                                            autoFocus
                                                            className="w-full px-2 py-1 text-xs border border-stone-400 rounded bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                                                            defaultValue={row.fields[col.key] || ''}
                                                            onBlur={e => {
                                                                updatePreviewCell(row.row_num, col.key, e.target.value);
                                                                setEditingCell(null);
                                                            }}
                                                            onKeyDown={e => {
                                                                if (e.key === 'Enter') { e.target.blur(); }
                                                                if (e.key === 'Escape') { setEditingCell(null); }
                                                            }}
                                                        />
                                                    ) : (
                                                        <div
                                                            className="px-2 py-1 text-xs text-stone-700 max-w-[200px] truncate cursor-text hover:bg-stone-100 rounded"
                                                            onClick={() => setEditingCell({ rowIdx: row.row_num, colKey: col.key })}
                                                            title="Click to edit"
                                                        >
                                                            {row.fields[col.key] || <span className="text-stone-300">—</span>}
                                                        </div>
                                                    )}
                                                </td>
                                            ))}
                                            <td className="px-3 py-1.5 text-center">
                                                {row.warning ? (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-amber-100 text-amber-700 cursor-help" title={row.warning}>
                                                        <AlertTriangle className="w-3 h-3" />
                                                        <span className="hidden sm:inline">{row.warning}</span>
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-emerald-100 text-emerald-700">
                                                        <CheckCircle2 className="w-3 h-3" />
                                                        <span className="hidden sm:inline">Ready</span>
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="flex items-center justify-between pt-2">
                        <Button variant="outline" onClick={() => setStep(3)}>
                            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Mapping
                        </Button>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" onClick={downloadPreviewCSV}>
                                <Download className="w-4 h-4 mr-1.5" /> Download CSV
                            </Button>
                            <Button onClick={() => setStep(5)} disabled={previewDataRows.total_rows === 0}>
                                Looks Good — Continue <ArrowRight className="w-4 h-4 ml-1.5" />
                            </Button>
                        </div>
                    </div>

                    <p className="text-[11px] text-stone-400 text-center">Click any cell to edit its value. Changes will be included when you import.</p>
                </div>
            )}

            {/* Step 5: Review & Send to Staging */}
            {step === 5 && !importResult && (
                <div className="space-y-4">
                    <div>
                        <h3 className="text-sm font-semibold text-stone-900">Review & Import to Staging</h3>
                        <p className="text-xs text-stone-500 mt-0.5">Data will be sent to the staging area for review before being committed to the CRM.</p>
                    </div>

                    <div className="bg-stone-50 rounded-lg p-4 space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="bg-white rounded-lg p-3 text-center border border-stone-200">
                                <div className="text-2xl font-bold text-stone-900">{totalRows}</div>
                                <div className="text-xs text-stone-500">Total Rows</div>
                            </div>
                            <div className="bg-white rounded-lg p-3 text-center border border-stone-200">
                                <div className="text-2xl font-bold text-stone-900">{mappings.filter(m => m.crm_field).length}</div>
                                <div className="text-xs text-stone-500">Fields Mapped</div>
                            </div>
                        </div>

                        <div className="space-y-3">
                            <div>
                                <label className="block text-xs font-medium text-stone-600 mb-1">List Name</label>
                                <input value={listName} onChange={e => setListName(e.target.value)} placeholder="e.g. Q2 2026 Prospects"
                                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-500" />
                            </div>

                            <div className="space-y-2">
                                <p className="text-xs font-medium text-stone-600 mb-1">When duplicates are found (checks both staging and CRM):</p>
                                <label className="flex items-start gap-2 text-sm cursor-pointer p-2 rounded-lg border border-stone-200 hover:bg-stone-50 transition-colors">
                                    <input type="radio" name="duplicateMode" value="skip" checked={duplicateMode === 'skip'}
                                        onChange={() => setDuplicateMode('skip')} className="mt-0.5" />
                                    <div>
                                        <span className="text-stone-800 font-medium">Skip duplicates</span>
                                        <p className="text-xs text-stone-500">Contacts already in staging or CRM (by email) will not be imported again</p>
                                    </div>
                                </label>
                                <label className="flex items-start gap-2 text-sm cursor-pointer p-2 rounded-lg border border-stone-200 hover:bg-stone-50 transition-colors">
                                    <input type="radio" name="duplicateMode" value="update" checked={duplicateMode === 'update'}
                                        onChange={() => setDuplicateMode('update')} className="mt-0.5" />
                                    <div>
                                        <span className="text-stone-800 font-medium">Update existing records</span>
                                        <p className="text-xs text-stone-500">Import all rows — duplicates will be flagged and merged when committed to CRM</p>
                                    </div>
                                </label>
                                <label className="flex items-start gap-2 text-sm cursor-pointer p-2 rounded-lg border border-stone-200 hover:bg-stone-50 transition-colors">
                                    <input type="radio" name="duplicateMode" value="import_all" checked={duplicateMode === 'import_all'}
                                        onChange={() => setDuplicateMode('import_all')} className="mt-0.5" />
                                    <div>
                                        <span className="text-stone-800 font-medium">Import all rows</span>
                                        <p className="text-xs text-stone-500">Stage all rows regardless of duplicates — review and resolve manually</p>
                                    </div>
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                        <p className="text-xs text-blue-700">Records will be added to the <strong>staging area</strong> where you can review, assign sales reps, and edit before committing to the CRM.</p>
                    </div>

                    <div className="flex items-center justify-between pt-2">
                        <Button variant="outline" onClick={() => setStep(4)}>
                            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Preview
                        </Button>
                        <Button onClick={executeImport} disabled={importing} className="bg-stone-900 hover:bg-stone-800">
                            {importing ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Sending to Staging...</> :
                            <><Upload className="w-4 h-4 mr-1.5" /> Send to Staging</>}
                        </Button>
                    </div>
                </div>
            )}

            {/* Import Results — Staging */}
            {importResult && (
                <div className="space-y-4">
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 text-center">
                        <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                        <h3 className="text-lg font-semibold text-emerald-900 mb-2">Sent to Staging</h3>
                        <p className="text-sm text-emerald-700 mb-4">Records are now in the staging area for review before committing to the CRM.</p>
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-4">
                            <div className="bg-white rounded-lg p-3 border border-stone-200">
                                <div className="text-xl font-bold text-stone-900">{importResult.total_rows}</div>
                                <div className="text-xs text-stone-500">Total Rows</div>
                            </div>
                            <div className="bg-white rounded-lg p-3 border border-emerald-200">
                                <div className="text-xl font-bold text-emerald-700">{importResult.staged_rows}</div>
                                <div className="text-xs text-stone-500">Staged for Review</div>
                            </div>
                            <div className="bg-white rounded-lg p-3 border border-blue-200">
                                <div className="text-xl font-bold text-blue-700">{importResult.assigned || 0}</div>
                                <div className="text-xs text-stone-500">Assigned to Reps</div>
                            </div>
                            <div className="bg-white rounded-lg p-3 border border-blue-200">
                                <div className="text-xl font-bold text-blue-700">{importResult.updated || 0}</div>
                                <div className="text-xs text-stone-500">Updated in Staging</div>
                            </div>
                            <div className={`bg-white rounded-lg p-3 border-2 ${importResult.skipped > 0 ? 'border-amber-400 bg-amber-50' : 'border-stone-200'}`}>
                                <div className={`text-xl font-bold ${importResult.skipped > 0 ? 'text-amber-700' : 'text-stone-400'}`}>{importResult.skipped || 0}</div>
                                <div className={`text-xs ${importResult.skipped > 0 ? 'text-amber-600 font-medium' : 'text-stone-500'}`}>Skipped (Duplicates)</div>
                            </div>
                        </div>
                        {importResult.skipped > 0 && (
                            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-left">
                                <p className="text-xs font-semibold text-amber-800 mb-2">Why were rows skipped?</p>
                                <p className="text-xs text-amber-700">Duplicates were detected by matching email addresses against both the staging area and existing CRM contacts.</p>
                                <p className="text-[11px] text-amber-600 mt-2 pt-2 border-t border-amber-200">
                                    Tip: Select "Import all rows" in duplicate handling to stage all rows regardless of duplicates.
                                </p>
                            </div>
                        )}
                        {importResult.unmatched_reps?.length > 0 && (
                            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-left">
                                <p className="text-xs font-semibold text-amber-800 mb-2">Unmatched Sales Reps</p>
                                <p className="text-xs text-amber-700 mb-2">The following rep names from the CSV could not be matched to CRM users. You can assign them manually in the staging area.</p>
                                <div className="flex flex-wrap gap-1.5">
                                    {importResult.unmatched_reps.map((rep, i) => (
                                        <span key={i} className="inline-flex px-2 py-0.5 text-[11px] font-medium rounded-full bg-amber-100 text-amber-800">{rep}</span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                        <p className="text-xs text-blue-700">
                            <strong>Next steps:</strong> Review records, assign sales reps, enrich with AI, and commit to the CRM when ready.
                            {importResult.list_name && <> Records were imported to list "<strong>{importResult.list_name}</strong>".</>}
                        </p>
                    </div>

                    <div className="flex justify-center gap-3">
                        <Button variant="outline" onClick={reset}>Import Another File</Button>
                        {onGoToStaging && (
                            <Button onClick={() => onGoToStaging(importResult.import_id)} className="bg-stone-900 hover:bg-stone-800">
                                <ArrowRight className="w-4 h-4 mr-1.5" /> Go to Staging Review
                            </Button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
